import { Controller } from "@hotwired/stimulus"
import Mustache from "mustache"
import db from "../field_db"

// Renders pending offline responses into the task list view.
//
// Reads pending_responses from IndexedDB and inserts them above the
// server-rendered list using the <template> element in the view.
// Items are removed once confirmed synced (field:sync-complete event).
//
// Usage:
//   data-controller="field-offline-list"
//   data-field-offline-list-target="template"   -- <template> with Mustache markup
//   data-field-offline-list-target="container"  -- element to prepend offline items into

export default class extends Controller {
  static targets = ["template", "container"]

  connect() {
    this._render()

    this._syncHandler   = () => this._refresh()
    this._onlineHandler = () => this._refresh()
    window.addEventListener("field:sync-complete", this._syncHandler)
    window.addEventListener("field:online", this._onlineHandler)
  }

  disconnect() {
    window.removeEventListener("field:sync-complete", this._syncHandler)
    window.removeEventListener("field:online", this._onlineHandler)
  }

  // -- private --

  async _render() {
    if (!this.hasTemplateTarget || !this.hasContainerTarget) return

    const pending = await db.pending_responses
      .where("status").anyOf(["pending", "failed"])
      .toArray()

    this._clearOfflineItems()
    if (pending.length === 0) return

    const templateHtml = this.templateTarget.innerHTML

    // Insert in reverse so newest-first ordering is preserved after prepend
    const sorted = pending.slice().sort((a, b) => a.queuedAt > b.queuedAt ? -1 : 1)
    sorted.forEach(record => {
      const html = Mustache.render(templateHtml, {
        responseId:     record.responseId,
        missionName:    record.missionName || `Response #${record.responseId}`,
        placeName:      record.placeName   || "",
        hasFailed:      record.status === "failed",
        // Card #4594 -- a queued row with a completeUrl is a finished mission
        // waiting to sync, not just a saved draft. Mustache's own section tags
        // do the branching in the template -- no HTML built here.
        hasCompleteUrl: !!record.completeUrl
      })
      const wrapper = document.createElement("div")
      wrapper.setAttribute("data-offline-response", record.responseId)
      wrapper.className = "block"
      wrapper.innerHTML = html
      this.containerTarget.prepend(wrapper)
    })
  }

  _refresh() {
    this._render()
  }

  _clearOfflineItems() {
    this.containerTarget.querySelectorAll("[data-offline-response]").forEach(el => el.remove())
  }
}
